import { create } from 'zustand';
import type { ImportSub } from '../../lib/data-passport/imports';
import type { CatalogueEntry } from '../../lib/data-passport/service';

/**
 * Data Passport's own state: module state, so it survives the panel closing and reopening (production's widget kept
 * its state while the app ran). Map visibility stays the map store's; layers added from other themes are
 * `addedLayers`; imports are overlays the engine draws.
 */

export type DpMode = 'guide' | 'import' | 'register';
export type ExSub = 'import' | 'export';
export type BrowseView = 'app' | 'data';
export type MsgType = 'success' | 'error' | 'info';

export interface Message {
  id: number;
  type: MsgType;
  text: string;
}

/** A layer of the active theme Data Passport switched on, with what it changed (put back on remove). */
export interface ThemeAdd {
  layerId: string;
  title: string;
  themeId: string;
  groupPath: string[];
  /** Visibility keys this add changed, with their values before it. */
  changed: Record<string, boolean>;
}

export interface ImportEntry {
  id: string;
  title: string;
  kind: 'url' | 'file';
  detail: string;
  subs: ImportSub[];
  /** Show the sub-layers the KML started switched off. */
  showHidden?: boolean;
}

export type CatalogueState = 'idle' | 'load' | 'ok' | 'err';

/** A layer or group of another theme that Search Pro or Layers Pro asked Browse to find (production's find). */
export interface PassportFind {
  themeId: string;
  title: string;
}

interface PassportState {
  dpMode: DpMode;
  /** The row Browse marks while its search still reads the find's title. */
  found: PassportFind | null;
  exSub: ExSub;
  browseView: BrowseView;
  search: string;
  /** Row details open, by layer id. */
  expanded: Record<string, boolean>;
  /** "More details" open, by layer id. */
  secondary: Record<string, boolean>;
  appThemes: Record<string, boolean>;
  appGroups: Record<string, boolean>;
  /** The pinned "Added to the map" list: null = auto (open while short). */
  addedOpen: boolean | null;
  messages: Message[];
  toast: Message | null;
  highlight: boolean;
  messagesOpen: boolean;
  /** Rows whose last add failed. */
  failed: Record<string, boolean>;
  themeAdds: Record<string, ThemeAdd>;
  imports: ImportEntry[];
  /** The next palette colour an import takes (production cycled its palette across imports). */
  palette: number;
  catalogue: CatalogueEntry[] | null;
  catalogueState: CatalogueState;
  set: (patch: Partial<Omit<PassportState, 'set' | 'toggle' | 'showMessage'>>) => void;
  toggle: (map: 'expanded' | 'secondary' | 'appThemes' | 'appGroups', key: string) => void;
  showMessage: (type: MsgType, text: string) => void;
}

let seq = 0;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
let highlightTimer: ReturnType<typeof setTimeout> | null = null;

export const usePassport = create<PassportState>()((set, get) => ({
  dpMode: 'guide',
  exSub: 'import',
  // Default App view: people open Browse looking for a layer they can name from the app (production's default).
  browseView: 'app',
  search: '',
  found: null,
  expanded: {},
  secondary: {},
  appThemes: {},
  appGroups: {},
  addedOpen: null,
  messages: [],
  toast: null,
  highlight: false,
  messagesOpen: false,
  failed: {},
  themeAdds: {},
  imports: [],
  palette: 0,
  catalogue: null,
  catalogueState: 'idle',
  set: (patch) => set(patch),
  toggle: (map, key) => set({ [map]: { ...get()[map], [key]: !get()[map][key] } }),
  showMessage: (type, text) => {
    const message = { id: ++seq, type, text };
    set({ messages: [...get().messages, message], toast: message, highlight: true });
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastTimer = null;
      if (get().toast?.id === message.id) set({ toast: null });
    }, 4000);
    if (highlightTimer) clearTimeout(highlightTimer);
    highlightTimer = setTimeout(() => {
      highlightTimer = null;
      set({ highlight: false });
    }, 3000);
  },
}));

/**
 * Show the Updates tab (the register) the next time the pane shows, or now if it is open: Information's "Open in
 * Data Passport". Production left a session flag for the widget to read as it mounted and fired an event for one
 * already mounted; the module store does both.
 */
export function showRegister(): void {
  usePassport.getState().set({ dpMode: 'register' });
}

/** Seed the Messages log with the help text, once (production's "Show help text"). */
export function seedHelp(text: string): void {
  const s = usePassport.getState();
  if (s.messages.some((m) => m.text === text)) return;
  s.set({ messages: [{ id: ++seq, type: 'info', text }, ...s.messages] });
}
